import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { API } from '../../config/api';
import axios from "axios";
import { Button } from "../../components/ui/button";
import { ArrowLeft, Trash2, Loader2, Shield } from "lucide-react";
import { format, parseISO } from "date-fns";
import { fmtUK } from "../../lib/dateUtils";
import { LOGO_URL } from "../../config/constants";

export default function DeletedSubmissions() {
  const navigate = useNavigate();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  useEffect(() => {
    fetchDeleted();
  }, []);
  const fetchDeleted = async () => {
    try {
      const userId = sessionStorage.getItem("bonsai_user_id");
      const resp = await axios.get(`${API}/submissions/deleted`, {
        headers: { "X-Bonsai-User-Id": userId }
      });
      setEntries(resp.data?.entries || []);
    } catch (err) {
      setError(err.response?.status === 403 ? "Director access required" : "Failed to load deleted submissions");
    } finally {
      setLoading(false);
    }
  };
  const fmt = (d) => fmtUK(d) === "-" ? "—" : fmtUK(d);
  if (error) {
    return (
      <div className="min-h-screen bg-[#F5F5F0] flex items-center justify-center">
        <div className="text-center">
          <Shield className="w-12 h-12 text-status-red mx-auto mb-4" />
          <h2 className="text-xl font-medium text-txt mb-2">{error}</h2>
          <Button onClick={() => navigate("/")} variant="outline" className="mt-4">Return to Dashboard</Button>
        </div>
      </div>
    );
  }
  return (
    <div className="min-h-screen bg-[#F5F5F0]" data-testid="deleted-submissions-page">
      <nav className="sticky top-0 z-50 w-full border-b bg-white/95 backdrop-blur">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 flex justify-between items-center h-14">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate("/")} className="flex items-center gap-2 hover:opacity-80">
              <img src={LOGO_URL} alt="Bonsai Law" className="w-8 h-8" />
              <span className="text-sm font-bold text-teal">Bonsai Law</span>
            </button>
            <span className="text-gray-300">/</span>
            <div className="flex items-center gap-1.5">
              <Trash2 className="w-4 h-4 text-status-red" />
              <span className="text-sm font-medium text-txt">Deletion Audit Log</span>
            </div>
          </div>
          <Button variant="ghost" onClick={() => navigate("/")} className="text-slate-600 hover:text-teal">
            <ArrowLeft className="w-4 h-4 mr-2" /> Dashboard
          </Button>
        </div>
      </nav>
      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-white">Deleted Submissions</h1>
          <p className="text-sm text-slate-500 mt-1">Audit trail of all submission deletions. Only directors can view this page.</p>
        </div>
        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin text-teal" />
          </div>
        ) : entries.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-md border border-navy-border">
            <Trash2 className="w-10 h-10 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500">No submissions have been deleted yet.</p>
          </div>
        ) : (
          <div className="glass-card shadow-sm overflow-hidden">
            <table className="w-full" data-testid="deleted-submissions-table">
              <thead>
                <tr className="border-b border-navy-border bg-navy-hover/60">
                  <th className="text-left px-4 py-3 text-xs font-medium text-slate-500 uppercase tracking-wider">Submission</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-slate-500 uppercase tracking-wider">Type</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-slate-500 uppercase tracking-wider">Deleted By</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-slate-500 uppercase tracking-wider">Deleted At</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-slate-500 uppercase tracking-wider">Reason</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {entries.map((e, i) => (
                  <tr key={i} className="hover:bg-navy-hover/60/50" data-testid={`deleted-entry-${i}`}>
                    <td className="px-4 py-3">
                      <div className="text-sm font-medium text-white">{e.primary_label || e.matter_reference || "—"}</div>
                      <div className="text-xs text-slate-400 font-mono">{e.submission_id?.slice(0, 8)}...</div>
                    </td>
                    <td className="px-4 py-3">
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-slate-100 text-txt">
                        {e.form_type?.replace(/_/g, " ") || "unknown"}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-600">{e.deleted_by}</td>
                    <td className="px-4 py-3 text-sm text-slate-500">{fmt(e.deleted_at)}</td>
                    <td className="px-4 py-3 text-sm text-slate-600 max-w-xs">
                      <p className="truncate" title={e.reason}>{e.reason}</p>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="px-4 py-3 border-t border-navy-border bg-navy-hover/60 text-xs text-slate-400">
              {entries.length} deletion{entries.length !== 1 ? "s" : ""} recorded
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
